import { micIsCarrying } from "../call/media-state.ts";
import { typedAgoIn } from "../chat/composer/typing.ts";
import { chosen, type Happening } from "./choosing.ts";
import { bursts, type Cue, type Voice } from "./cue.ts";
import { type Device, theDevice } from "./device.ts";
import { mayPlay, type Verdict } from "./gate.ts";
import { planFor } from "./score.ts";
import { inForce, loudnessOf, type SoundSettings, soundSettingsFor } from "./settings.ts";
import { type Burst, closed, closesAt, offered, QUIET } from "./throttle.ts";

let device: Device = theDevice;
let account: string | null = null;
let reduced = false;
let said: SoundSettings | null = null;
let onScreen: string | null = null;
let burst: Burst = QUIET;

let heldVoice: Voice = "human";
let summarising: ReturnType<typeof setTimeout> | null = null;

export function soundsFor(who: string | null, reducedMotion: boolean): void {
  account = who;
  reduced = reducedMotion;
  said = who ? soundSettingsFor(who) : null;

  burst = QUIET;
  if (summarising !== null) clearTimeout(summarising);
  summarising = null;
}

export function readTheSettings(): void {
  said = account ? soundSettingsFor(account) : null;
}

export function settingsNow(): SoundSettings {
  return inForce(said, reduced);
}

export function lookingAt(conversation: string | null): void {
  onScreen = conversation;
}

export function useThisDevice(next: Device): void {
  device = next;
}

export function wakeTheDevice(): void {
  device.wake();
}

export function hush(): void {
  device.hush();
}

function hasFocus(): boolean {
  if (typeof document === "undefined") return false;
  return document.visibilityState === "visible" && document.hasFocus();
}

function hearing(conversation: string, mine: boolean): Parameters<typeof mayPlay>[1] {
  return {
    settings: settingsNow(),
    mine,
    focused: onScreen === conversation && hasFocus(),
    typedAgo: typedAgoIn(conversation),
    speaking: micIsCarrying(),
    silent: device.silent(),
  };
}

function sound(cue: Cue, voice: Voice, gain: number): void {
  device.play(planFor(cue, voice, gain), gain);
}

function summarise(): void {
  summarising = null;
  const shut = closed(burst, Date.now());
  burst = shut.burst;
  if (shut.summary === 0) return;
  const verdict = mayPlay("summary", hearing(onScreen ?? "", false));
  if (verdict.play) sound("summary", heldVoice, verdict.gain);
}

function holdForTheSummary(voice: Voice): void {
  heldVoice = voice;
  const shuts = closesAt(burst);
  if (shuts === null) return;
  if (summarising !== null) clearTimeout(summarising);
  summarising = setTimeout(summarise, Math.max(0, shuts - Date.now()));
}

export function hears(happening: Happening): Verdict {
  const { cue, voice, mine } = chosen(happening);
  const verdict = mayPlay(cue, hearing(happening.conversation, mine));
  if (!verdict.play) return verdict;
  if (!bursts(cue)) {
    sound(cue, voice, verdict.gain);
    return verdict;
  }
  const offer = offered(burst, Date.now());
  burst = offer.burst;
  if (offer.play) {
    sound(cue, voice, verdict.gain);
    return verdict;
  }
  holdForTheSummary(voice);
  return { play: false, why: "one cue for the burst, then a summary", gain: verdict.gain };
}

export function previewOf(cue: Cue, voice: Voice): void {
  device.wake();
  const gain = loudnessOf(settingsNow());
  sound(cue, voice, gain);
}
